/**
 * W-010 — Subjects list. Stories: US-A02.
 *
 * AC4 drag-to-reorder, publish state, stream tags, search.
 */
import { DeleteButton, EditButton, List, useTable } from '@refinedev/antd'
import { useCan, useUpdate } from '@refinedev/core'
import { Input, Space, Switch, Tag, Typography } from 'antd'
import { useState } from 'react'
import { ModelInsights, OrderableTable, StatusTag, UnpublishConfirmDialog } from '@/components'
import { recordAudit } from '@/lib/audit'
import { persistOrder } from '@/lib/order'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { Locale, Program, Stream, Subject } from '@/types'
import { usePrograms } from '../reference-data/usePrograms'
import { useStreams } from './useStreams'

interface SubjectListRow extends Subject {
  subject_streams?: { stream_id: string }[]
}

export const SubjectListPage = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const { tableProps, setFilters, tableQueryResult } = useTable<Subject>({
    resource: 'subjects',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 50 },
    meta: { select: '*, subject_streams(stream_id)' },
    syncWithLocation: true,
  })

  const { streams } = useStreams()
  const { programs } = usePrograms()
  const { data: canEdit } = useCan({ resource: 'subjects', action: 'edit' })
  const { mutate: update } = useUpdate()
  const [unpublishTarget, setUnpublishTarget] = useState<Subject | null>(null)

  const streamProgram = new Map(streams.map(stream => [stream.id, stream.program_id]))
  const rows = ((tableProps.dataSource ?? []) as SubjectListRow[]).map(subject => ({
    ...subject,
    program_ids: Array.from(
      new Set(
        (subject.subject_streams ?? [])
          .map(row => streamProgram.get(row.stream_id))
          .filter((id): id is string => Boolean(id)),
      ),
    ),
  }))

  const handleReorder = (ordered: Subject[]) => {
    void persistOrder('subjects', ordered)
      .then(() => tableQueryResult.refetch())
      .catch((error: unknown) => {
        console.error('[subjects] reorder failed', error)
        void tableQueryResult.refetch()
      })
  }

  // Publishing goes through the form, which runs the bilingual guard of
  // US-A02 AC6; the list only offers the reverse.
  const togglePublish = (subject: Subject, next: boolean) => {
    if (!next) setUnpublishTarget(subject)
  }

  return (
    <List
      title={translate('admin.pages.subjects')}
      headerButtons={({ defaultButtons }) => defaultButtons}
      canCreate
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="subjects" />
        <Input.Search
          placeholder={translate('admin.screens.subjects.searchPlaceholder')}
          allowClear
          onSearch={value =>
            setFilters(
              value
                ? [
                    {
                      operator: 'or',
                      value: [
                        { field: 'name_ar', operator: 'contains', value },
                        { field: 'name_fr', operator: 'contains', value },
                      ],
                    },
                  ]
                : [],
              'replace',
            )
          }
          style={{ maxWidth: 420 }}
        />

        <Typography.Text type="secondary">
          {translate('admin.screens.subjects.reorderHint')}
        </Typography.Text>

        <OrderableTable<Subject>
          {...tableProps}
          dataSource={rows}
          onReorder={handleReorder}
          reorderDisabled={!canEdit?.can}
          columns={[
            {
              title: translate('admin.columns.nameAr'),
              dataIndex: 'name_ar',
              render: (value: string) => <span dir="rtl">{value}</span>,
            },
            { title: translate('admin.columns.nameFr'), dataIndex: 'name_fr' },
            {
              title: translate('admin.columns.streams'),
              dataIndex: 'stream_ids',
              render: (_: unknown, record: Subject) => (
                <Space wrap size={4}>
                  {(record.stream_ids ?? []).map(id => (
                    <Tag key={id}>{streamLabel(streams, id)}</Tag>
                  ))}
                </Space>
              ),
            },
            {
              title: translate('admin.columns.programs'),
              dataIndex: 'program_ids',
              render: (_: unknown, record: Subject) => (
                <Space wrap size={4}>
                  {(record.program_ids ?? []).map(id => (
                    <Tag key={id}>{programLabel(programs, id, locale)}</Tag>
                  ))}
                </Space>
              ),
            },
            {
              title: translate('admin.columns.state'),
              dataIndex: 'is_published',
              render: (value: boolean, record: Subject) => (
                <Space>
                  <StatusTag kind="published" value={value} />
                  <Switch
                    size="small"
                    checked={value}
                    disabled={!canEdit?.can || !value}
                    onChange={next => togglePublish(record, next)}
                  />
                </Space>
              ),
            },
            {
              title: translate('admin.columns.actions'),
              dataIndex: 'id',
              render: (_: unknown, record: Subject) => (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton
                    hideText
                    size="small"
                    recordItemId={record.id}
                    confirmTitle={translate('admin.screens.subjects.deleteConfirm')}
                    confirmOkText={translate('common.delete')}
                    confirmCancelText={translate('common.cancel')}
                    onSuccess={() =>
                      void recordAudit({
                        action: 'subject.delete',
                        target: 'subjects',
                        targetId: record.id,
                        before: { name_fr: record.name_fr, name_ar: record.name_ar },
                      })
                    }
                  />
                </Space>
              ),
            },
          ]}
        />
      </Space>

      {/* WD-2 */}
      <UnpublishConfirmDialog
        open={unpublishTarget !== null}
        targetLabel={`« ${unpublishTarget?.name_fr ?? ''} »`}
        onCancel={() => setUnpublishTarget(null)}
        onConfirm={async () => {
          if (!unpublishTarget) return
          update({
            resource: 'subjects',
            id: unpublishTarget.id,
            values: { is_published: false },
          })
          await recordAudit({
            action: 'subject.unpublish',
            target: 'subjects',
            targetId: unpublishTarget.id,
            before: { is_published: true },
            after: { is_published: false },
          })
          setUnpublishTarget(null)
        }}
      />
    </List>
  )
}

function streamLabel(streams: Stream[], id: string): string {
  return streams.find(stream => stream.id === id)?.name_fr ?? id.slice(0, 8)
}

function programLabel(programs: Program[], id: string, locale: Locale): string {
  const program = programs.find(item => item.id === id)
  if (!program) return id.slice(0, 8)
  return locale === 'ar' ? program.name_ar : program.name_fr
}
